import type { ReactNode } from 'react';
import { Button } from '@/shared/ui/button';
import { styles } from '@/modules/agreements/platform/pages/document-create-page/styles';
import type { DocumentProgress } from '@/modules/agreements/platform/pages/document-create-page/types';

/**
 * "The document already exists" — shown when an earlier write succeeded and a
 * later one failed.
 *
 * This is the honest surface for a non-atomic flow. The API has no delete for a
 * document, so a failure after step 1 leaves a real, half-authored document in
 * the deployment. The operator is told which parts exist and given the one
 * action that resolves it: open the document and finish there.
 *
 * It is a `caution` wash, not a `critical` one. Nothing is broken and no data is
 * lost — but the work is incomplete, and an operator who is not told will author
 * the document a second time.
 */
export function PartialNotice(props: {
  progress: DocumentProgress;
  onOpenDocument: (documentId: string) => void;
}): ReactNode {
  const { progress, onOpenDocument } = props;
  if (!progress.documentId) return null;

  const missing = progress.versionId ? 'requirement' : 'version';

  return (
    <div className={styles.partialNotice} role="status">
      <p className={styles.partialNoticeTitle}>
        The document was created, but its {missing} was not.
      </p>
      <p className={styles.partialNoticeBody}>
        {progress.versionId
          ? 'The document and its first version exist. Only the requirement is missing, so nothing asks a subject to act yet.'
          : 'The document exists with no published version, so it has no content yet.'}{' '}
        Nothing was lost — finish authoring it from the document page.
      </p>
      <div className={styles.partialNoticeActions}>
        <Button size="sm" onClick={() => onOpenDocument(progress.documentId as string)}>
          Open the document
        </Button>
      </div>
    </div>
  );
}
